import AsyncStorage from '@react-native-async-storage/async-storage';
import * as npipeClient from './npipeClient';
import { printNpipeReceipts } from './npipeReceipt';

const NOVO_TERMINAL = 'Novo Terminal';
const TIMEOUT_CODES = ['TIMEOUT', 'UNREACHABLE', 'BAD_RESPONSE'];

let inFlight = false;

/**
 * True when Settings > Payments > card provider is "Novo Terminal".
 * @returns {Promise<boolean>}
 */
export const isNpipeEnabled = async () => {
    try {
        return (await AsyncStorage.getItem('selectedCardTerminal')) === NOVO_TERMINAL;
    } catch (_) {
        return false;
    }
};

/**
 * Major units (e.g. forints) to the minor units the NPIPE spec wants (x100).
 * @param {number|string} amount
 * @returns {number} integer minor units
 */
export const toMinorUnits = (amount) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) {
        return NaN;
    }
    return Math.round(n * 100);
};

/**
 * Unique invoice reference derived from the order code (unique per attempt).
 * @param {string} reference order code
 * @param {number} [now]
 * @returns {string}
 */
export const buildInvoiceReference = (reference, now = Date.now()) => {
    const base = String(reference || '').replace(/^#/, '').trim() || 'order';
    return `#${base}-${now.toString(36).slice(-5)}`;
};

const isUnknownOutcome = (error) => {
    if (!error) {
        return false;
    }
    const code = String(error.code || '').toUpperCase();
    const kind = String(error.kind || '').toLowerCase();
    return TIMEOUT_CODES.includes(code) || kind === 'timeout' || kind === 'unreachable' || Number(error.responseCode) === 11;
};

const failure = (message, extra = {}) => ({
    ok: false,
    approved: false,
    cancelled: false,
    result: null,
    receiptsPrinted: false,
    error: { code: 'ERROR', message, kind: 'error', ...extra },
});

const safeCall = async (fn, ...args) => {
    try {
        const result = await fn(...args);
        return result || failure('Empty response from terminal');
    } catch (e) {
        return failure(e?.message || String(e), { code: e?.code || 'ERROR' });
    }
};

const printSafe = async (result, opts) => {
    try {
        const r = await printNpipeReceipts(result, opts);
        return r !== false;
    } catch (_) {
        return false;
    }
};

/**
 * Take a card payment on the configured NPIPE terminal. Never throws.
 * On timeout / unreachable the outcome is UNKNOWN: do not auto-retry the charge;
 * error.unknownOutcome is true and error.offerLastTransactionCheck asks the UI
 * to offer reprint / last-transaction check (npipeReprintLast).
 * @param {{moduleKey?:'pos'|'driver'|'service', amount:number, currency?:string, reference:string, tip?:number, pttpType?:string, onStatus?:Function}} params amount/tip in major units
 * @returns {Promise<{ok:boolean, approved:boolean, cancelled:boolean, result:Object|null, receiptsPrinted:boolean, error:Object|null}>}
 */
export const payWithNpipe = async ({ moduleKey = 'pos', amount, currency, reference, tip, pttpType, onStatus } = {}) => {
    const status = (s) => {
        try {
            onStatus && onStatus(s);
        } catch (_) { /* UI callback must not break payment */ }
    };

    if (!(await isNpipeEnabled())) {
        return failure('Novo Terminal is not the selected card provider', { code: 'NOT_ENABLED', kind: 'config' });
    }
    if (inFlight) {
        return failure('A card payment is already in progress', { code: 'IN_FLIGHT', kind: 'busy' });
    }
    const minor = toMinorUnits(amount);
    if (!Number.isFinite(minor) || minor <= 0) {
        return failure('Invalid amount', { code: 'INVALID_AMOUNT', kind: 'config' });
    }

    inFlight = true;
    try {
        let config = null;
        try {
            config = await npipeClient.loadNpipeConfig(moduleKey);
        } catch (_) {
            config = null;
        }
        if (!config) {
            return failure('No card terminal configured', { code: 'NO_CONFIG', kind: 'config' });
        }

        const tipMinor = config.tipEnabled && Number(tip) > 0 ? toMinorUnits(tip) : 0;
        const params = {
            config,
            amount: minor,
            currency: currency || config.currency,
            reference: buildInvoiceReference(reference),
            invoiceReference: buildInvoiceReference(reference),
        };
        if (pttpType) {
            params.pttpType = pttpType;
        }
        if (tipMinor > 0) {
            params.tip = tipMinor;
            params.amountGratuity = tipMinor;
        }

        status('waiting_for_terminal');
        const result = await safeCall(npipeClient.purchase, params);
        const approved = !!(result.ok && result.approved);
        const cancelled = !approved && !result.error && Number(result.responseCode) !== 0 && /cancel/i.test(String(result.resultState || ''));
        let error = approved ? null : (result.error || { code: String(result.responseCode ?? 'DECLINED'), message: 'Payment not approved', kind: 'declined' });
        if (error && isUnknownOutcome(error)) {
            error = { ...error, unknownOutcome: true, offerLastTransactionCheck: true };
        }

        let receiptsPrinted = false;
        if (approved || (result.receipts && result.receipts.length) || (result.printoutLines && result.printoutLines.length)) {
            status('printing');
            receiptsPrinted = await printSafe(result, { moduleKey, config });
        }
        status('done');
        return { ok: !!result.ok, approved, cancelled, result, receiptsPrinted, error };
    } catch (e) {
        return failure(e?.message || String(e));
    } finally {
        inFlight = false;
    }
};

const runAction = async (name, moduleKey, extra, convertAmount) => {
    let config = null;
    try {
        config = await npipeClient.loadNpipeConfig(moduleKey);
    } catch (_) {
        config = null;
    }
    if (!config) {
        return failure('No card terminal configured', { code: 'NO_CONFIG', kind: 'config' });
    }
    const params = { ...(extra || {}), config };
    if (convertAmount && params.amount !== undefined) {
        params.amount = toMinorUnits(params.amount);
    }
    // eslint-disable-next-line import/namespace -- `name` is one of the fixed wrapper names below
    const result = await safeCall(npipeClient[name], params);
    if (result.receipts?.length || result.printoutLines?.length) {
        result.receiptsPrinted = await printSafe(result, { moduleKey, config });
    }
    return result;
};

/**
 * Reverse (void) a transaction; omit params for "last transaction".
 * @param {{moduleKey?:string, amount?:number, referenceNumber?:string, reference?:string}} [params] amount in major units
 * @returns {Promise<Object>} normalized result, never throws
 */
export const reverseNpipe = ({ moduleKey = 'pos', ...rest } = {}) => runAction('reversal', moduleKey, rest, true);

/**
 * Refund (not available for Sca).
 * @param {{moduleKey?:string, amount:number, referenceNumber?:string, reference?:string}} params amount in major units
 * @returns {Promise<Object>} normalized result, never throws
 */
export const refundNpipe = ({ moduleKey = 'pos', ...rest } = {}) => runAction('refund', moduleKey, rest, true);

/**
 * End of day / settlement.
 * @param {string} [moduleKey]
 * @returns {Promise<Object>} normalized result, never throws
 */
export const npipeEndOfDay = (moduleKey = 'pos') => runAction('endOfDay', moduleKey);

/**
 * Terminal server connection test.
 * @param {string} [moduleKey]
 * @returns {Promise<Object>} normalized result, never throws
 */
export const npipeConnectionTest = (moduleKey = 'pos') => runAction('connectionTest', moduleKey);

/**
 * Reprint last receipt (Wallee only) / last-transaction check.
 * @param {string} [moduleKey]
 * @returns {Promise<Object>} normalized result, never throws
 */
export const npipeReprintLast = (moduleKey = 'pos') => runAction('reprintLast', moduleKey);

/**
 * Whether a payment is currently running.
 * @returns {boolean}
 */
export const isNpipePaymentInFlight = () => inFlight;
